import type { CSSProperties, ReactElement, ReactNode } from "react";
import { motion } from "framer-motion";
import { LayoutGrid } from "lucide-react";
import { Button, Stack, Surface, Text } from "@primitives/index";
import { useViewport } from "@primitives/viewport";
import { color, radius, space, useOverlayMotion } from "@tokens/index";

export interface SignInGateProps {
    readonly appName: string;
    readonly onSignIn: () => void;
}

/**
 * Unauthenticated landing composition.
 *
 * Unaware of MSAL, auth state, or routing: it accepts an opaque `onSignIn`
 * callback so the experience shell owns the auth-mode decision.
 */
export function SignInGate(props: SignInGateProps): ReactElement {
    return (
        <AuthCard>
            <Stack direction="column" gap={6} align="center">
                <BrandMark size={56} />

                <Stack direction="column" gap={2} align="center">
                    <Text as="h1" role="title" weight="bold">
                        {props.appName}
                    </Text>
                    <Text as="p" role="bodyLg" tone="muted">
                        Sign in to continue.
                    </Text>
                </Stack>

                <Button variant="primary" size="lg" onClick={props.onSignIn}>
                    Sign in with Microsoft
                </Button>
            </Stack>
        </AuthCard>
    );
}

// ---------------------------------------------------------------------------
// AuthCard — the one "you are not in the app" surface.
//
// Shared by the sign-in and re-auth gates so the two read as one state
// machine. A flat centered card: hairline border, `xl` radius, no shadow.
// ---------------------------------------------------------------------------

export interface AuthCardProps {
    readonly role?: string;
    readonly "aria-live"?: "polite" | "assertive";
    readonly "aria-label"?: string;
    readonly children: ReactNode;
}

const authFrameStyle: CSSProperties = {
    position: "relative",
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: space[4],
    overflow: "auto",
};

export function AuthCard({ children, ...rest }: AuthCardProps): ReactElement {
    const { isCompact } = useViewport();
    const enter = useOverlayMotion("dialog");

    return (
        <div style={authFrameStyle}>
            <motion.div
                initial={enter.initial}
                animate={enter.animate}
                transition={enter.transition}
                style={{ width: "100%", maxWidth: 440 }}
            >
                <Surface
                    elevation={1}
                    borderRadius="xl"
                    padding={isCompact ? 5 : 6}
                    style={{ width: "100%" }}
                    {...rest}
                >
                    {children}
                </Surface>
            </motion.div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// BrandMark — the Einstein mark. The gradient is the brand's one permitted
// gradient fill on a control-sized object.
// ---------------------------------------------------------------------------

export interface BrandMarkProps {
    /** Edge length in px. Default `32`. */
    readonly size?: number;
}

export function BrandMark({ size = 32 }: BrandMarkProps): ReactElement {
    const iconSize = Math.round(size / 2);
    return (
        <span
            aria-hidden="true"
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: size,
                height: size,
                borderRadius: size >= 48 ? radius.lg : radius.md,
                background: color.gradient.brandMark,
                color: color.accent.onPrimary,
                flexShrink: 0,
            }}
        >
            <LayoutGrid size={iconSize} strokeWidth={2.25} />
        </span>
    );
}
